import { Link, Button, Badge, Card, CardContent, ArrowRight, Check, TrendingUp, useAuth } from "@/pages/shared";
import { Helmet } from "react-helmet-async";
import { CLAIMS } from "@/config/claims";

// ILLUSTRATIVE EXAMPLE ONLY — this is not a customer result and contains no
// revenue, reach, click or conversion figures. It shows how the workflow fits
// together for a fictional persona.
const EXAMPLE = {
  persona: "A sustainable home & lifestyle creator (fictional persona)",
  before: "Spending 3–4 hours a day writing posts by hand, with no way to see which posts actually drove interest.",
  after: "Generates a week of drafts in one sitting, reviews and schedules them, and uses tracked links plus a lead form to see which topics resonate.",
  timeline: [
    { day: "Day 1", event: "Signs up and describes their brand voice in Settings so drafts come out on-brand." },
    { day: "Day 3", event: "Runs a Revenue Campaign: a set of social posts, a blog draft and an email generated around one product theme, then edits and approves each draft." },
    { day: "Day 7", event: "Creates a tracked short link for a product they recommend and adds it to scheduled posts, with the disclosure line inserted from Settings." },
    { day: "Day 14", event: "Publishes a lead-capture form for a free starter guide and links it from their bio and posts." },
    { day: "Day 21", event: "Adds an AI-generated vertical video clip (Pro plan) to their short-form workflow." },
    { day: "Day 30", event: "Reviews the analytics page: clicks per link, leads per form, and which content types performed — then plans the next month around what worked." },
  ],
  features_used: ["Revenue Campaigns wizard", "Tracked affiliate links", "Monetization Score on every post", "AI video clips (Pro)", "Lead capture forms", "Brand voice training"],
};

export default function CaseStudyPage() {
  const { user } = useAuth();
  const ctaTo = user ? "/dashboard" : "/register";
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>Example walkthrough — a 30-day content workflow in Creatara</title>
        <meta name="description" content="An illustrative, fictional example of how a solopreneur could use Creatara over 30 days: generation, scheduling, tracked links and lead capture. Not a customer result." />
      </Helmet>

      <nav className="border-b border-white/5 sticky top-0 z-40 bg-background/80 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold">Creatara</Link>
          <Link to={ctaTo}><Button className="bg-violet-600 hover:bg-violet-700 rounded-full" data-testid="casestudy-signup-btn">{user ? "Go to Dashboard" : "Try it free"}</Button></Link>
        </div>
      </nav>

      <section className="pt-16 pb-8 px-6">
        <div className="max-w-4xl mx-auto">
          <Badge className="mb-4 bg-amber-500/15 text-amber-400 border-amber-500/30" data-testid="casestudy-illustrative-badge">Illustrative example — not a customer result</Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
            What 30 days in Creatara <span className="text-violet-400">could look like</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl">{EXAMPLE.persona}. This walkthrough is fictional and contains no earnings or performance figures — it exists to show how the pieces of the workflow fit together.</p>
        </div>
      </section>

      <section className="px-6 pb-12">
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="glass border-violet-600/20"><CardContent className="p-6">
            <p className="text-sm text-violet-400 font-semibold mb-2 uppercase tracking-wider">The problem</p>
            <p className="text-muted-foreground leading-relaxed">{EXAMPLE.before}</p>
          </CardContent></Card>
          <Card className="glass border-teal-600/20"><CardContent className="p-6">
            <p className="text-sm text-teal-400 font-semibold mb-2 uppercase tracking-wider">The workflow</p>
            <p className="text-muted-foreground leading-relaxed">{EXAMPLE.after}</p>
          </CardContent></Card>
        </div>
      </section>

      <section className="px-6 pb-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2"><TrendingUp className="w-6 h-6 text-violet-400" /> An example 30-day timeline</h2>
          <div className="space-y-3" data-testid="casestudy-timeline">
            {EXAMPLE.timeline.map((t, i) => (
              <Card key={i} className="glass">
                <CardContent className="p-5 flex items-start gap-4">
                  <div className="flex-shrink-0 w-20 text-sm font-bold text-violet-400">{t.day}</div>
                  <p className="text-sm text-muted-foreground flex-1 leading-relaxed">{t.event}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4 italic">Reminder: this is an illustrative example of using the product, not a report of anyone's results. Outcomes depend entirely on your niche, audience and effort.</p>
        </div>
      </section>

      <section className="px-6 pb-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-4">Features used in this walkthrough</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {EXAMPLE.features_used.map((f) => (
              <div key={f} className="flex items-center gap-3 p-3 rounded-lg glass">
                <Check className="w-5 h-5 text-teal-500 flex-shrink-0" />
                <p className="text-sm">{f}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 pb-20">
        <div className="max-w-3xl mx-auto text-center bg-gradient-to-br from-violet-600/20 to-violet-900/20 border border-violet-500/30 rounded-2xl p-10">
          <h2 className="text-3xl font-bold mb-3">Try the workflow yourself.</h2>
          <p className="text-muted-foreground mb-6">The Free plan includes {CLAIMS.freeAllowance.generations} generations per {CLAIMS.freeAllowance.period} — enough to run this whole loop once.</p>
          <Link to={ctaTo}>
            <Button size="lg" className="bg-violet-600 hover:bg-violet-700 rounded-full px-8" data-testid="casestudy-cta-btn">
              {user ? "Go to Dashboard" : "Start free"} <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          {!user && <p className="text-xs text-muted-foreground mt-3">No credit card. Pro from £29/mo.</p>}
        </div>
      </section>
    </div>
  );
}
